import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/siteData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Spreadsheet Guide', path: '/article/sugargoo-spreadsheet-guide' },
    { name: 'Shipping Guide', path: '/article/sugargoo-shipping-cost-guide' },
    { name: 'Warehouse Guide', path: '/article/sugargoo-warehouse-guide' },
    { name: 'Coupons & Deals', path: '/article/sugargoo-coupon-discount-guide' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-file-list-3-line text-lg"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-foreground-900 tracking-tight whitespace-nowrap">
              Sugargoo Spreadsheet
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="px-3 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 rounded-lg hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2.5">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-background-100 text-foreground-700 rounded-lg border border-background-200/60 hover:bg-background-200 hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
            >
              <i className="ri-shopping-bag-3-line"></i>
              Shopping Guide
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider whitespace-nowrap">Navigation</p>
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-sm font-medium text-foreground-600 hover:text-foreground-900 rounded-lg hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
              >
                {link.name === 'Home' && <i className="ri-home-4-line text-foreground-400"></i>}
                {link.name === 'Spreadsheet Guide' && <i className="ri-file-list-3-line text-foreground-400"></i>}
                {link.name === 'Shipping Guide' && <i className="ri-ship-line text-foreground-400"></i>}
                {link.name === 'Warehouse Guide' && <i className="ri-building-line text-foreground-400"></i>}
                {link.name === 'Coupons & Deals' && <i className="ri-coupon-line text-foreground-400"></i>}
                {link.name}
              </Link>
            ))}

            <div className="border-t border-background-200/60 my-2"></div>
            <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider whitespace-nowrap">Product Categories</p>
            <div className="grid grid-cols-2 gap-1 px-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 text-xs font-medium text-foreground-600 hover:text-foreground-900 rounded-lg hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  <i className="ri-shopping-bag-3-line text-foreground-400 text-xs"></i>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="border-t border-background-200/60 my-2"></div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="mx-2 px-4 py-3 text-sm font-semibold text-center bg-background-100 text-foreground-700 rounded-lg border border-background-200/60 hover:bg-background-200 transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-shopping-bag-3-line"></i>
              Shopping Guide
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="mx-2 mt-1.5 px-4 py-3 text-sm font-semibold text-center bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}